import React, { useState, useEffect, useMemo } from 'react';
import './App.css';
import WeekSelector from './components/WeekSelector';
import LeaderboardTable from './components/LeaderboardTable';
import MetricSelector from './components/MetricSelector';
import LeaderboardChart from './components/LeaderboardChart';
import LoginPage from './components/LoginPage';
import UserInfoBar from './components/UserInfoBar';
import HorizontalTabs from './components/HorizontalTabs';
import AIOverviewTable from './components/AIOverviewTable';
import PeriodToggle, { ReportingPeriod } from './components/PeriodToggle';
import { fetchWeeklyData, fetchMonthlyData, fetch4WeeklyData, fetch4WeeklyAIData, clear4WeeklyAICache, sortUsersByMetric, fetchNonAIData, fetch4WeeklyNonAIData } from './services/dataService';
import { enrichLeaderboardWithSpend } from './services/cursorSpendService';
import { getStoredToken, getStoredProfile, isAuthenticated, logout } from './services/authService';
import { fetchUserTeamMembership } from './services/teamMembershipService';
import { getCentralPSFilterManagerId } from './utils/centralPSAccess';
import { sortPersonalizedLeaderboard } from './services/personalizedDataService';
import { getMondayMidnightUTC, formatTimestampToDate, getFourWeeklyStartMonday, isAtLeastFourWeeksBack } from './services/dailyUsageApiService';
import { fetchComprehensiveData, fetchManagerTeamData, fetchNonManagerTeamData, fetchCentralPSTeams, extractCentralPSUserIds } from './services/multiTeamDataService';
import { getFilteredLeaderboardData, filterActivitiesForCentralPS } from './services/teamFilteringService';
import { getCurrentWeekMonday } from './services/crossoverService';
import { processComprehensiveAIData } from './services/aiOverviewService';
import { getFrontendCache, clearFrontendCache } from './services/frontendCacheService';
import { LeaderboardEntry, AuthState, PersonalizedLeaderboardEntry, DataMode, ViewMode, ProcessedAIData, NonAIDataResult, CrossoverTeamMemberActivity } from './types';
import { TeamMemberActivityWithContext } from './types/teamTypes';
import NonAIAppsSection from './components/NonAIAppsSection';
import TeamAISection from './components/TeamAISection';
import { TeamAIDataService, TeamAIMemberData, RollingPeriodInfo } from './services/teamAIDataService';
// Import export option types
import { LeaderboardExportOptions } from './services/leaderboardExportService';
import { AIOverviewExportOptions } from './services/aiOverviewExportService';
import { NonAIAppsExportOptions } from './services/nonAIAppsExportService';
import { TeamAIExportOptions } from './services/teamAIExportService';
import WorksmartAISection from './components/WorksmartAISection';
import { WorksmartAIMemberData, WorksmartTeamStats } from './types/worksmartTypes';
import { fetchWorksmartAIData, enrichWorksmartDataWithTeamContext, calculateWorksmartTeamStats, sortWorksmartData } from './services/worksmartAIService';
import { WorksmartAIExportOptions } from './services/worksmartAIExportService';

function App() {
  // Authentication state
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    token: null,
    managerId: null,
    userProfile: null,
    teamMembership: null,
    loading: false,
    error: null
  });
  
  // Central PS access state
  const [hasCentralPSAccess, setHasCentralPSAccess] = useState<boolean>(false);

  // Leaderboard state
  const [selectedWeek, setSelectedWeek] = useState<string>('');
  const [selectedMetric, setSelectedMetric] = useState<string>('chatTotalApplies');
  const [users, setUsers] = useState<LeaderboardEntry[]>([]);
  const [personalizedUsers, setPersonalizedUsers] = useState<PersonalizedLeaderboardEntry[]>([]);
  const [spendCycleDateRange, setSpendCycleDateRange] = useState<{ cycleStart: Date; cycleEnd: Date; label: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dataMode, setDataMode] = useState<DataMode>('basic');
  
  // 4-weekly reporting state
  const [reportingPeriod, setReportingPeriod] = useState<ReportingPeriod>('weekly');
  const [viewMode, setViewMode] = useState<ViewMode>('cursor');
  const [comprehensiveData, setComprehensiveData] = useState<any>(null);
  const [aiData, setAIData] = useState<ProcessedAIData | null>(null);
  
  // Team AI Leaderboard state
  const [teamAIData, setTeamAIData] = useState<TeamAIMemberData[]>([]);
  const [teamAILoading, setTeamAILoading] = useState(false);
  const [teamAIError, setTeamAIError] = useState<string | null>(null);
  const [teamAISortBy, setTeamAISortBy] = useState<string>('aiEffectivenessScore');
  const [teamAIPeriodInfo, setTeamAIPeriodInfo] = useState<RollingPeriodInfo | null>(null);
  const [teamAIPeriodMode, setTeamAIPeriodMode] = useState<'last-4-weeks' | 'current-board'>('current-board');

  // Worksmart AI state
  const [worksmartAIData, setWorksmartAIData] = useState<WorksmartAIMemberData[]>([]);
  const [worksmartAIStats, setWorksmartAIStats] = useState<WorksmartTeamStats>({
    totalUsers: 0,
    averageAIRatio: 0,
    averageAIRatioPercent: 0,
    highestAIUser: null,
    lowestAIUser: null
  });
  const [worksmartAILoading, setWorksmartAILoading] = useState(false);
  const [worksmartAIError, setWorksmartAIError] = useState<string | null>(null);
  const [worksmartAISortBy, setWorksmartAISortBy] = useState<keyof WorksmartAIMemberData>('aiRatioPercent');

  // Non-AI Apps state
  const [nonAIData, setNonAIData] = useState<NonAIDataResult>({
    teamMemberApplications: [],
    topApplications: []
  });
  const [nonAILoading, setNonAILoading] = useState(false);
  const [nonAIError, setNonAIError] = useState<string | null>(null);

  // Ensure basic mode data is always sorted when displayed
  const basicUsers = useMemo(() => {
    if (dataMode === 'basic' && users.length > 0) {
      return sortUsersByMetric(users, selectedMetric as keyof Omit<LeaderboardEntry, 'userId' | 'email'>);
    }
    return users;
  }, [users, selectedMetric, dataMode]);

  /**
   * Initialize authentication state from localStorage on app start
   */
  useEffect(() => {
    const initializeAuth = async () => {
      if (isAuthenticated()) {
        const token = getStoredToken();
        const userProfile = getStoredProfile();
        
        if (token && userProfile) {
          const restoredAuthState = {
            isAuthenticated: true,
            token,
            managerId: userProfile.managerAvatar?.id || null,
            userProfile,
            teamMembership: null,
            loading: false,
            error: null
          };
          setAuthState(restoredAuthState);
          console.log('Restored authentication from localStorage');
          
          // Fetch team membership for restored session
          if (token) {
            console.log('🔍 [DEBUG] Fetching team membership for restored session...');
            try {
              const teamMembership = await fetchUserTeamMembership(token);
              setAuthState(prev => ({
                ...prev,
                teamMembership
              }));
              setHasCentralPSAccess(teamMembership.hasCentralPSAccess);
              console.log('✅ [DEBUG] Team membership loaded for restored session:', {
                teamsCount: teamMembership.teams.length,
                hasCentralPSAccess: teamMembership.hasCentralPSAccess
              });
            } catch (error) {
              console.error('🚨 [DEBUG] Failed to load team membership for restored session:', error);
              setError(`Failed to load team membership: ${error instanceof Error ? error.message : 'Unknown error'}. Some features may be unavailable.`);
            }
          }
        }
      }
    };

    initializeAuth();
  }, []);

  /**
   * Update Central PS access when team membership changes
   */
  useEffect(() => {
    if (authState.teamMembership) {
      setHasCentralPSAccess(authState.teamMembership.hasCentralPSAccess);
    } else {
      setHasCentralPSAccess(false);
    }
  }, [authState.teamMembership]);

  /**
   * Check if user can access Central PS mode
   * New rule: User has access if they are a MEMBER (not just manager) of any team owned by 2371741
   * Uses team membership data from authState (fetched on login)
   */
  const canAccessCentralPS = (): boolean => {
    // Use team membership data from authState
    return authState.teamMembership?.hasCentralPSAccess || false;
  };

  /**
   * Check if user can access Team AI Leaderboard (managerId === 2359404)
   */
  const canAccessTeamAI = (): boolean => {
    if (!authState.managerId) {
      console.log('🚨 [TEAM-AI] Cannot check Team AI access - missing managerId');
      return false;
    }
    
    const hasAccess = authState.managerId === 2359404;
    
    console.log('🔍 [TEAM-AI] Team AI access check:', {
      userManagerId: authState.managerId,
      expectedManagerId: 2359404,
      hasAccess
    });
    
    return hasAccess;
  };

  /**
   * Check if user is a manager (has managerId)
   */
  const isManager = (): boolean => {
    return authState.managerId !== null && authState.managerId !== undefined;
  };

  /** CPS access via backend email allowlist (e.g. moved org; not on CPS rosters). */
  const isCentralPSAllowlistUser = (): boolean => {
    return authState.teamMembership?.centralPSAccessViaAllowlist === true;
  };

  /**
   * Get available data modes for current user based on permissions
   */
  const getAvailableDataModes = (): DataMode[] => {
    if (!authState.isAuthenticated) {
      return ['basic']; // Show basic option for login prompt
    }

    const modes: DataMode[] = ['basic']; // Everyone gets basic

    // My Team available to ALL users (if they have team membership)
    if (authState.teamMembership && authState.teamMembership.teams.length > 0) {
      modes.push('my-team');
    }

    // Central PS available if user is member of Central PS teams
    if (hasCentralPSAccess) {
      modes.push('central-ps');
    }

    return modes;
  };

  /**
   * Get available data modes for AI Overview based on permissions
   */
  const getAvailableAIDataModes = (): DataMode[] => {
    if (!authState.isAuthenticated) {
      return []; // No AI Overview for unauthenticated users
    }

    if (!isManager() && !isCentralPSAllowlistUser()) {
      return [];
    }

    const modes: DataMode[] = [];

    if (isManager()) {
      modes.push('my-team');
    }

    // Central PS access if user is member of Central PS teams
    if (hasCentralPSAccess) {
      modes.push('central-ps');
    }

    return modes;
  };

  /**
   * Get available data modes for Non-AI Apps based on permissions
   */
  const getAvailableNonAIDataModes = (): DataMode[] => {
    if (!authState.isAuthenticated) {
      return []; // No Non-AI Apps for unauthenticated users
    }

    if (!isManager() && !isCentralPSAllowlistUser()) {
      return [];
    }

    const modes: DataMode[] = [];

    if (isManager()) {
      modes.push('my-team');
    }

    // Central PS access if user is member of Central PS teams
    if (hasCentralPSAccess) {
      modes.push('central-ps');
    }

    return modes;
  };

  /**
   * Get available data modes for Team AI based on permissions
   */
  const getAvailableTeamAIDataModes = (): DataMode[] => {
    if (!authState.isAuthenticated || !isManager() || !canAccessTeamAI()) {
      return []; // No Team AI for non-managers or unauthorized users
    }

    const modes: DataMode[] = [];

    // Managers with Team AI access always get My Team for Team AI
    modes.push('my-team');

    // Central PS managers with Team AI access also get Central PS for Team AI
    if (hasCentralPSAccess) {
      modes.push('central-ps');
    }

    return modes;
  };

  /**
   * Get available data modes for Worksmart AI based on permissions
   * NEW: Available to all users with team membership (not just managers)
   */
  const getAvailableWorksmartAIDataModes = (): DataMode[] => {
    if (!authState.isAuthenticated) {
      return []; // No Worksmart AI for unauthenticated users
    }

    const modes: DataMode[] = [];

    // My Team available to all users with team membership
    if (authState.teamMembership && authState.teamMembership.teams.length > 0) {
      modes.push('my-team');
    }

    // Central PS access if user is member of Central PS teams
    if (hasCentralPSAccess) {
      modes.push('central-ps');
    }

    return modes;
  };

  /**
   * Check if AI Overview tab should be visible
   */
  const isAIOverviewAvailable = (): boolean => {
    return authState.isAuthenticated && (isManager() || isCentralPSAllowlistUser());
  };

  /**
   * Check if Non-AI Apps tab should be visible
   */
  const isNonAIAppsAvailable = (): boolean => {
    return authState.isAuthenticated && (isManager() || isCentralPSAllowlistUser());
  };

  /**
   * Check if Team AI tab should be visible
   */
  const isTeamAIAvailable = (): boolean => {
    return authState.isAuthenticated && isManager() && canAccessTeamAI();
  };

  /**
   * Check if Worksmart AI tab should be visible
   * NEW: Available for all users with team membership (not just managers)
   */
  const isWorksmartAIAvailable = (): boolean => {
    if (!authState.isAuthenticated || authState.teamMembership === null) {
      return false;
    }
    return (
      authState.teamMembership.teams.length > 0 ||
      authState.teamMembership.hasCentralPSAccess
    );
  };

  /**
   * Get default data mode for a given view mode
   */
  const getDefaultDataMode = (view: ViewMode): DataMode => {
    if (view === 'cursor') {
      return 'basic'; // Cursor always starts with basic
    } else if (view === 'ai-overview') {
      // AI Overview starts with My Team (or Central PS if that's the only option)
      const availableModes = getAvailableAIDataModes();
      return availableModes.includes('my-team') ? 'my-team' : availableModes[0] || 'basic';
    } else if (view === 'non-ai-apps') {
      // Non-AI Apps starts with My Team (or Central PS if that's the only option)
      const availableModes = getAvailableNonAIDataModes();
      return availableModes.includes('my-team') ? 'my-team' : availableModes[0] || 'basic';
    } else if (view === 'team-ai') {
      // Team AI starts with My Team (or Central PS if that's the only option)
      const availableModes = getAvailableTeamAIDataModes();
      return availableModes.includes('my-team') ? 'my-team' : availableModes[0] || 'basic';
    } else if (view === 'worksmart-ai') {
      // Worksmart AI starts with My Team (or Central PS if that's the only option)
      const availableModes = getAvailableWorksmartAIDataModes();
      return availableModes.includes('my-team') ? 'my-team' : availableModes[0] || 'basic';
    } else {
      return 'basic';
    }
  };

  /**
   * Handle successful authentication
   */
  const handleAuthSuccess = async (newAuthState: AuthState) => {
    setAuthState(newAuthState);
    
    // Fetch team membership information (no persistent caching)
    if (newAuthState.token) {
      console.log('🔍 [DEBUG] Starting team membership fetch...');
      try {
        console.log('🔍 [DEBUG] Calling fetchUserTeamMembership with token:', newAuthState.token.substring(0, 20) + '...');
        const teamMembership = await fetchUserTeamMembership(newAuthState.token);
        console.log('✅ [DEBUG] Team membership fetched successfully:', teamMembership);
        
        setAuthState(prev => ({
          ...prev,
          teamMembership
        }));
        
        console.log('✅ [DEBUG] Team membership set in state');
        
        // Set Central PS access based on team membership
        setHasCentralPSAccess(teamMembership.hasCentralPSAccess);
        
        console.log('✅ Authentication successful with team membership:', {
          teams: teamMembership.teams.length,
          isManager: teamMembership.isManager,
          hasCentralPSAccess: teamMembership.hasCentralPSAccess,
          teamsDetail: teamMembership.teams.map(t => ({
            teamId: t.teamId,
            teamName: t.teamName,
            teamOwnerId: t.teamOwnerId,
            role: t.role,
            isCentralPS: t.teamOwnerId === 2371741
          }))
        });
        
        // Debug: Check for Central PS teams
        const centralPSTeams = teamMembership.teams.filter(t => {
          const ownerId = typeof t.teamOwnerId === 'string' ? parseInt(t.teamOwnerId, 10) : t.teamOwnerId;
          return ownerId === 2371741;
        });
        if (centralPSTeams.length > 0) {
          console.log('✅ [DEBUG] Found Central PS teams:', centralPSTeams.map(t => ({
            teamId: t.teamId,
            teamName: t.teamName,
            teamOwnerId: t.teamOwnerId
          })));
        } else {
          console.log('⚠️ [DEBUG] No Central PS teams found. User teams:', teamMembership.teams.map(t => ({
            teamId: t.teamId,
            teamName: t.teamName,
            teamOwnerId: t.teamOwnerId,
            teamOwnerIdType: typeof t.teamOwnerId
          })));
        }
        
        console.log('🔍 [DEBUG] Central PS Access Flag:', teamMembership.hasCentralPSAccess ? 'Y' : 'N');
      } catch (error) {
        console.error('🚨 [DEBUG] Failed to load team membership:', error);
        console.error('🚨 [DEBUG] Error details:', {
          message: error instanceof Error ? error.message : 'Unknown error',
          stack: error instanceof Error ? error.stack : undefined,
          error: error
        });
        // Show error to user - team membership is critical for access control
        setError(`Failed to load team membership: ${error instanceof Error ? error.message : 'Unknown error'}. Some features may be unavailable.`);
        
        // Set team membership to null explicitly so UI shows the error state
        setAuthState(prev => ({
          ...prev,
          teamMembership: null
        }));
      }
    }
    
    setDataMode('basic'); // Default to basic view after login
  };

  /**
   * Handle logout with cache clearing
   */
  const handleLogout = () => {
    logout();
    
    // Clear frontend cache on logout
    clearFrontendCache();
    
    // Clear 4-weekly AI cache on logout
    clear4WeeklyAICache();
    
    setAuthState({
      isAuthenticated: false,
      token: null,
      managerId: null,
      userProfile: null,
      teamMembership: null, // Clear team membership
      loading: false,
      error: null
    });
    setDataMode('basic');
    setViewMode('cursor');
    setPersonalizedUsers([]);
    setComprehensiveData(null);
    setAIData(null);
    setUsers([]);
    setError(null);
    console.log('Logged out successfully');
  };


  /**
   * Load basic leaderboard data (authenticated mode) with frontend caching
   * Enhanced to support dynamic field selection and monthly reporting
   */
  const loadBasicData = async (weekDate: string) => {
    if (!authState.isAuthenticated || !authState.token) {
      throw new Error('Authentication required for basic data access');
    }

    const periodLabel = reportingPeriod === '4weekly' ? '4-week period' : 'week';
    console.log(`📊 [BASIC] Loading basic leaderboard data for ${periodLabel}:`, weekDate);
    
    // Use frontend cache for basic data
    const frontendCache = getFrontendCache();
    
    // Static mode: support both weekly and 4-weekly data
    const cacheKey = `basic-data-${reportingPeriod}`;
    const data = await frontendCache.getCachedOrFetch(
        cacheKey,
        weekDate,
        async () => {
          if (reportingPeriod === '4weekly') {
            console.log('📡 [BASIC] Cache miss, fetching 4-weekly data');
            return await fetch4WeeklyData(weekDate, 'basic', {
              token: authState.token!,
              managerId: authState.managerId
            });
          } else {
            console.log('📡 [BASIC] Cache miss, fetching weekly data');
            return await fetchWeeklyData(weekDate);
          }
        }
      );
      
      console.log(`✅ [BASIC] ${reportingPeriod} data aggregated:`, {
        period: reportingPeriod,
        totalUsers: data.length,
        topUser: data.length > 0 ? data[0].email : 'None'
      });
      
    const { entries: dataWithSpend, cycleDateRange } = await enrichLeaderboardWithSpend(data);
    setSpendCycleDateRange(cycleDateRange ?? null);
    const sortedData = sortUsersByMetric(dataWithSpend, selectedMetric as keyof Omit<LeaderboardEntry, 'userId' | 'email'>);
    setUsers(sortedData);
  };

  /**
   * Load comprehensive data (for Central PS mode only)
   */
  const loadCentralPSData = async (weekDate: string) => {
    if (!authState.isAuthenticated || !authState.token) {
      throw new Error('Authentication required for Central PS data');
    }
    if (!hasCentralPSAccess || !authState.teamMembership?.hasCentralPSAccess) {
      throw new Error('Access denied: Central PS access requires CPS team membership or authorized access');
    }

    const cpsFilterManagerId = getCentralPSFilterManagerId(authState);

    const periodLabel = reportingPeriod === '4weekly' ? '4-week period' : 'week';
    console.log(`🏢 [CENTRAL-PS] Loading Central PS leaderboard data for ${periodLabel}:`, weekDate);
    
    // Convert week date to Monday date format for API
    const mondayTimestamp = getMondayMidnightUTC(weekDate);
    const mondayDate = formatTimestampToDate(mondayTimestamp);
    
    console.log('📅 [CENTRAL-PS] Monday date for API:', mondayDate);
    console.log('👤 [CENTRAL-PS] Using CPS filter managerId:', cpsFilterManagerId);
    
    // Check if we should use 4-weekly comprehensive data fetching or weekly
    if (reportingPeriod === '4weekly') {
      // Use new 4-weekly comprehensive data fetching
      console.log('📡 [CENTRAL-PS] Using 4-weekly comprehensive data fetching');
      const comprehensiveData = await fetch4WeeklyData(weekDate, 'central-ps', {
        token: authState.token!,
        managerId: cpsFilterManagerId
      });
      
      // Enrich with spend and sort
      const { entries: withSpend, cycleDateRange } = await enrichLeaderboardWithSpend(comprehensiveData as PersonalizedLeaderboardEntry[]);
      setSpendCycleDateRange(cycleDateRange ?? null);
      const sortedData = sortPersonalizedLeaderboard(
        withSpend,
        selectedMetric as keyof PersonalizedLeaderboardEntry
      );
      setPersonalizedUsers(sortedData);
      
      // Process AI data for 4-weekly mode
      try {
        const aiData4Weekly = await fetch4WeeklyAIData(weekDate, 'central-ps', {
          token: authState.token!,
          managerId: cpsFilterManagerId
        });
        setAIData(aiData4Weekly);
        console.log('✅ [CENTRAL-PS] 4-weekly AI data loaded:', aiData4Weekly);
      } catch (error) {
        console.warn('⚠️ [CENTRAL-PS] Failed to load 4-weekly AI data:', error);
        setAIData(null);
      }
      
      console.log('✅ [CENTRAL-PS] 4-weekly comprehensive data loaded:', {
        totalUsers: comprehensiveData.length
      });
      
      return;
    }

    // Load both cursor data and comprehensive team data in parallel with frontend caching (weekly mode)
    const frontendCache = getFrontendCache();
    const [cursorData, comprehensiveTeamData] = await Promise.all([
      frontendCache.getCachedOrFetch(
        'basic-data',
        weekDate,
        async () => {
          console.log('📡 [CENTRAL-PS] Cache miss, fetching cursor data');
          return await fetchWeeklyData(weekDate);
        }
      ),
      frontendCache.getCachedOrFetch(
        'comprehensive-data',
        weekDate,
        async () => {
          console.log('📡 [CENTRAL-PS] Cache miss, fetching comprehensive data');
          return await fetchComprehensiveData(mondayDate);
        }
      )
    ]);

    console.log('✅ [CENTRAL-PS] Data loaded successfully:', {
      cursorUsersCount: cursorData.length,
      teamsCount: comprehensiveTeamData.teams.length,
      teamActivitiesCount: comprehensiveTeamData.activities.length
    });
    
    // Check for aggregation issues
    const cursorEmails = cursorData.map((u: LeaderboardEntry) => u.email);
    const uniqueCursorEmails = new Set(cursorEmails);
    if (cursorEmails.length !== uniqueCursorEmails.size) {
      console.error('🚨 [CENTRAL-PS] Data aggregation issue - duplicate users detected', {
        totalRecords: cursorEmails.length,
        uniqueUsers: uniqueCursorEmails.size
      });
    }

    // Store comprehensive data for permission checking
    setComprehensiveData(comprehensiveTeamData);
    
    // Apply filtering to AI data for Central PS users (same filtering as leaderboard)
    const filteredActivitiesForAI = filterActivitiesForCentralPS(
      comprehensiveTeamData.activities,
      cpsFilterManagerId,
      comprehensiveTeamData.teams,
      'global'
    );

    // Process AI data using filtered activities only
    const processedAIData = processComprehensiveAIData(filteredActivitiesForAI);
    setAIData(processedAIData);
    
    // Apply global filtering for Central PS
    const filteredData = getFilteredLeaderboardData(
      comprehensiveTeamData,
      cursorData,
      cpsFilterManagerId,
      'central-ps',
      'global'
    );
    
    // For Central PS, filter out cursor-only users (users without team data)
    const finalData = filteredData.filter(user => user.cursorTimeMinutes > 0);
    const { entries: finalDataWithSpend, cycleDateRange } = await enrichLeaderboardWithSpend(finalData);
    setSpendCycleDateRange(cycleDateRange ?? null);
    setPersonalizedUsers(finalDataWithSpend);
    
    console.log('🎯 [CENTRAL-PS] Created leaderboard and AI data:', {
      totalFilteredUsers: filteredData.length,
      finalUsers: finalData.length,
      cursorOnlyUsersRemoved: filteredData.length - finalData.length,
      aiUsersProcessed: filteredActivitiesForAI.length
    });
  };

  /**
   * Load My Team data (uses manager-specific API with user's token)
   */
  const loadMyTeamData = async (weekDate: string) => {
    if (!authState.isAuthenticated || !authState.token) {
      throw new Error('Authentication required for My Team data');
    }

    // Check if user has team membership
    const teamMembership = authState.teamMembership;
    if (!teamMembership || teamMembership.teams.length === 0) {
      throw new Error('No team membership found');
    }

    const isUserManager = teamMembership.isManager;
    const userTeamInfo = teamMembership.teams[0]; // Use first team

    const periodLabel = reportingPeriod === '4weekly' ? '4-week period' : 'week';
    console.log(`👥 [MY-TEAM] Loading My Team leaderboard data for ${periodLabel}:`, weekDate, {
      isManager: isUserManager,
      teamId: userTeamInfo.teamId,
      teamName: userTeamInfo.teamName
    });
    
    // Convert week date to Monday date format for API
    const mondayTimestamp = getMondayMidnightUTC(weekDate);
    const mondayDate = formatTimestampToDate(mondayTimestamp);
    
    console.log('📅 [MY-TEAM] Monday date for API:', mondayDate);
    console.log('👤 [MY-TEAM] User role:', isUserManager ? 'Manager' : 'Team Member');
    
    // Check if we should use 4-weekly comprehensive data fetching or weekly
    if (reportingPeriod === '4weekly') {
      // Use new 4-weekly comprehensive data fetching
      console.log('📡 [MY-TEAM] Using 4-weekly comprehensive data fetching');
      const comprehensiveData = await fetch4WeeklyData(weekDate, 'my-team', {
        token: authState.token!,
        managerId: authState.managerId!
      });
      
      // Enrich with spend and sort
      const { entries: withSpend, cycleDateRange } = await enrichLeaderboardWithSpend(comprehensiveData as PersonalizedLeaderboardEntry[]);
      setSpendCycleDateRange(cycleDateRange ?? null);
      const sortedData = sortPersonalizedLeaderboard(
        withSpend,
        selectedMetric as keyof PersonalizedLeaderboardEntry
      );
      setPersonalizedUsers(sortedData);
      
      // Process AI data for 4-weekly mode
      try {
        const aiData4Weekly = await fetch4WeeklyAIData(weekDate, 'my-team', {
          token: authState.token!,
          managerId: authState.managerId!
        });
        setAIData(aiData4Weekly);
        console.log('✅ [MY-TEAM] 4-weekly AI data loaded:', aiData4Weekly);
      } catch (error) {
        console.warn('⚠️ [MY-TEAM] Failed to load 4-weekly AI data:', error);
        setAIData(null);
      }
      
      console.log('✅ [MY-TEAM] 4-weekly comprehensive data loaded:', {
        totalUsers: comprehensiveData.length
      });
      
      return;
    }

    // Load cursor data and team data in parallel with frontend caching (weekly mode)
    const frontendCache = getFrontendCache();
    
    let cursorData: any;
    let teamData: any;
    
    if (isUserManager && authState.managerId) {
      // Manager mode: Use existing manager team data API
      console.log('👔 [MY-TEAM] Loading data for manager');
      
      [cursorData, teamData] = await Promise.all([
        frontendCache.getCachedOrFetch(
          'basic-data',
          weekDate,
          async () => {
            console.log('📡 [MY-TEAM] Cache miss, fetching cursor data');
            return await fetchWeeklyData(weekDate);
          }
        ),
        frontendCache.getCachedOrFetch(
          'manager-team-data',
          weekDate,
          async () => {
            console.log('📡 [MY-TEAM] Cache miss, fetching manager team data');
            return await fetchManagerTeamData(authState.managerId!, mondayDate, authState.token!);
          },
          authState.managerId?.toString() // Use managerId for cache key specificity
        )
      ]);
    } else {
      // Non-manager mode: Use new non-manager team data API
      console.log('👤 [MY-TEAM] Loading data for non-manager team member');
      
      [cursorData, teamData] = await Promise.all([
        frontendCache.getCachedOrFetch(
          'basic-data',
          weekDate,
          async () => {
            console.log('📡 [MY-TEAM] Cache miss, fetching cursor data');
            return await fetchWeeklyData(weekDate);
          }
        ),
        frontendCache.getCachedOrFetch(
          'non-manager-team-data',
          weekDate,
          async () => {
            console.log('📡 [MY-TEAM] Cache miss, fetching non-manager team data');
            return await fetchNonManagerTeamData(
              userTeamInfo.teamId,
              userTeamInfo.managerId,
              mondayDate,
              authState.token!
            );
          },
          `team-${userTeamInfo.teamId}` // Use teamId for cache key specificity
        )
      ]);
    }

    console.log('✅ [MY-TEAM] Data loaded successfully:', {
      cursorUsersCount: cursorData.length,
      teamMembersCount: teamData.activities.length,
      userRole: isUserManager ? 'Manager' : 'Member'
    });
    
    // Check for aggregation issues
    const cursorEmails = cursorData.map((u: LeaderboardEntry) => u.email);
    const uniqueCursorEmails = new Set(cursorEmails);
    if (cursorEmails.length !== uniqueCursorEmails.size) {
      console.error('🚨 [MY-TEAM] Data aggregation issue - duplicate users detected', {
        totalRecords: cursorEmails.length,
        uniqueUsers: uniqueCursorEmails.size
      });
    }

    // Apply team filtering for My Team (API already filtered, no additional filtering needed)
    const effectiveManagerId = isUserManager && authState.managerId ? authState.managerId : teamData.managerId;
    const filteredData = getFilteredLeaderboardData(
      teamData,
      cursorData,
      effectiveManagerId,
      'my-team',
      'team'
    );
    
    // Process AI data for My Team (no additional filtering needed as API already filtered)
    const processedAIData = processComprehensiveAIData(teamData.activities);
    setAIData(processedAIData);
    
    // Enrich with spend and show all team members (even those without cursor data)
    const { entries: filteredDataWithSpend, cycleDateRange } = await enrichLeaderboardWithSpend(filteredData);
    setSpendCycleDateRange(cycleDateRange ?? null);
    setPersonalizedUsers(filteredDataWithSpend);
    
    console.log('🎯 [MY-TEAM] Created leaderboard and AI data:', {
      totalTeamMembers: filteredData.length,
      withCursorData: filteredData.filter(user => user.cursorTimeMinutes > 0).length,
      withoutCursorData: filteredData.filter(user => user.cursorTimeMinutes === 0).length,
      aiUsersProcessed: teamData.activities.length
    });
  };

  /**
   * Load Non-AI application data
   */
  const loadNonAIData = async (weekDate: string) => {
    console.log('📱 [NON-AI] Loading Non-AI data for:', weekDate, 'mode:', dataMode, 'period:', reportingPeriod);
    
    setNonAILoading(true);
    setNonAIError(null);
    
    try {
      let result;
      
      if (reportingPeriod === '4weekly') {
        console.log('📱 [NON-AI] Using 4-weekly Non-AI data fetch');
        result = await fetch4WeeklyNonAIData(
          weekDate,
          dataMode,
          authState.token || undefined,
          authState.managerId || undefined
        );
      } else {
        console.log('📱 [NON-AI] Using weekly Non-AI data fetch');
        result = await fetchNonAIData(
          weekDate,
          dataMode,
          authState.token || undefined,
          authState.managerId || undefined
        );
      }
      
      setNonAIData(result);
      
      console.log('✅ [NON-AI] Non-AI data loaded successfully:', {
        teamMembers: result.teamMemberApplications.length,
        topApplications: result.topApplications.length,
        dataMode,
        reportingPeriod
      });
      
    } catch (error) {
      console.error('🚨 [NON-AI] Error loading Non-AI data:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load non-AI application data';
      setNonAIError(errorMessage);
    } finally {
      setNonAILoading(false);
    }
  };

  /**
   * Load Team AI Leaderboard data - 4-week rolling analysis
   */
  const loadTeamAIData = async (weekDate: string) => {
    console.log('🏆 [TEAM-AI] Loading Team AI data for:', weekDate, 'mode:', dataMode, 'period:', teamAIPeriodMode);
    
    setTeamAILoading(true);
    setTeamAIError(null);
    
    try {
      // Calculate rolling 4-week period based on current mode
      const periodInfo = TeamAIDataService.calculateRollingPeriod(weekDate, teamAIPeriodMode);
      setTeamAIPeriodInfo(periodInfo);
      
      console.log('🏆 [TEAM-AI] Rolling period:', periodInfo);
      
      // Fetch cursor data for all 4 weeks
      const cursorDataPromises = periodInfo.weekDates.map(async (date) => {
        try {
          return await fetchWeeklyData(date);
        } catch (error) {
          console.warn(`🏆 [TEAM-AI] Failed to fetch cursor data for week ${date}:`, error);
          return null;
        }
      });
      
      // Fetch crossover data for all 4 weeks based on data mode
      const crossoverDataPromises = periodInfo.weekDates.map(async (date) => {
        try {
          if (dataMode === 'central-ps') {
            const result = await fetchComprehensiveData(date);
            return result.activities; // Extract the activities array from MultiTeamDataResult
          } else if (dataMode === 'my-team' && authState.token && authState.managerId) {
            const result = await fetchManagerTeamData(authState.managerId, date, authState.token);
            return result.activities; // Extract the activities array from MultiTeamDataResult
          } else {
            console.warn(`🏆 [TEAM-AI] Team AI not available for data mode: ${dataMode}`);
            return [];
          }
        } catch (error) {
          console.warn(`🏆 [TEAM-AI] Failed to fetch crossover data for week ${date}:`, error);
          return null;
        }
      });
      
      // Execute all API calls in parallel
      const [cursorResults, crossoverResults] = await Promise.all([
        Promise.all(cursorDataPromises),
        Promise.all(crossoverDataPromises)
      ]);
      
      console.log('🏆 [TEAM-AI] Data fetched:', {
        cursorWeeks: cursorResults.filter((r: LeaderboardEntry[] | null) => r !== null).length,
        crossoverWeeks: crossoverResults.filter((r: TeamMemberActivityWithContext[] | null) => r !== null).length,
        totalWeeks: periodInfo.weekDates.length
      });
      
      // Process the data using TeamAIDataService
      const teamMembers = await TeamAIDataService.processTeamAIData(
        cursorResults,
        crossoverResults,
        periodInfo
      );
      
      setTeamAIData(teamMembers);
      
      console.log(`🏆 [TEAM-AI] Processed ${teamMembers.length} team members for Team AI leaderboard`);
      
    } catch (error) {
      console.error('🏆 [TEAM-AI] Error loading Team AI data:', error);
      setTeamAIError(`Failed to load Team AI data: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setTeamAILoading(false);
    }
  };

  /**
   * Handle Team AI sorting
   */
  const handleTeamAISortChange = (metric: string) => {
    setTeamAISortBy(metric);
    
    // Sort the team AI data
    const sortedData = [...teamAIData].sort((a, b) => {
      const aValue = getMetricValue(a, metric);
      const bValue = getMetricValue(b, metric);
      
      // Handle string comparison for names
      if (metric === 'name') {
        return aValue.localeCompare(bValue);
      }
      
      // Numeric comparison (descending order for scores)
      return bValue - aValue;
    });
    
    setTeamAIData(sortedData);
    console.log('🏆 [TEAM-AI] Sorted by:', metric);
  };

  /**
   * Handle Team AI period mode change
   */
  const handleTeamAIPeriodModeChange = (newMode: 'last-4-weeks' | 'current-board') => {
    console.log('🏆 [TEAM-AI] Period mode changing from', teamAIPeriodMode, 'to', newMode);
    setTeamAIPeriodMode(newMode);
    
    // Reload data with new period mode
    if (isTeamAIAvailable()) {
      const currentMonday = getCurrentWeekMonday();
      loadTeamAIData(currentMonday);
    }
  };
  
  /**
   * Get metric value from team member data for sorting
   */
  const getMetricValue = (member: TeamAIMemberData, metric: string): any => {
    switch (metric) {
      case 'name':
        return member.name;
      case 'aiEffectivenessScore':
        return member.metrics.aiEffectivenessScore;
      case 'cursorEfficiency':
        return member.metrics.cursorEfficiency;
      case 'aiToolEffectiveness':
        return member.metrics.aiToolEffectiveness;
      case 'overallAIAdoption':
        return member.metrics.overallAIAdoption;
      case 'totalAITime':
        return member.metrics.totalAITime;
      case 'uniqueAIToolsCount':
        return member.metrics.uniqueAIToolsCount;
      case 'primaryAITool':
        return member.metrics.primaryAITool;
      default:
        return member.metrics.aiEffectivenessScore;
    }
  };

  /**
   * Load Worksmart AI data for the selected week
   */
  const loadWorksmartAIData = async (weekDate: string) => {
    console.log('📊 [WORKSMART-AI] Loading Worksmart AI data for:', weekDate, 'mode:', dataMode);
    
    setWorksmartAILoading(true);
    setWorksmartAIError(null);
    
    try {
      // Extract Central PS user IDs from comprehensive data if available
      let centralPSUserIds: number[] | undefined;
      let teamDataForEnrichment: any = null;
      
      if (dataMode === 'central-ps') {
        // For Central PS mode, use teams endpoint to get userIds (much faster than comprehensive data)
        console.log('📊 [WORKSMART-AI] Extracting Central PS user IDs from teams/assignments endpoint');
        centralPSUserIds = await extractCentralPSUserIds();
        
        // Still fetch comprehensive data for enrichment (but only if we got userIds)
        if (centralPSUserIds.length > 0) {
          const mondayTimestamp = getMondayMidnightUTC(weekDate);
          const mondayDate = formatTimestampToDate(mondayTimestamp);
          
          console.log('📊 [WORKSMART-AI] Fetching comprehensive data for enrichment:', mondayDate);
          try {
            const comprehensiveTeamData = await fetchComprehensiveData(mondayDate);
            teamDataForEnrichment = comprehensiveTeamData;
          } catch (error) {
            console.warn('⚠️ [WORKSMART-AI] Failed to fetch comprehensive data for enrichment, continuing without it:', error);
            // Continue without enrichment data - userIds are already extracted
          }
        }
        
        console.log('📊 [WORKSMART-AI] Extracted Central PS user IDs:', {
          count: centralPSUserIds.length,
          sampleUserIds: centralPSUserIds.slice(0, 10),
          hasEnrichmentData: !!teamDataForEnrichment
        });
      } else if (dataMode === 'my-team') {
        // For My Team mode, fetch comprehensive data first
        const mondayTimestamp = getMondayMidnightUTC(weekDate);
        const mondayDate = formatTimestampToDate(mondayTimestamp);
        
        if (authState.token && authState.managerId) {
          const teamData = await fetchManagerTeamData(authState.managerId, mondayDate, authState.token);
          
          // Extract user IDs from team data
          // For Worksmart AI, use candidate.userId (not candidate.id) as it matches xo.user_id in Worksmart database
          const userIds = new Set<number>();
          teamData.activities.forEach((activity: any) => {
            // Try shallow path first
            let userId = activity.assignment?.candidate?.userId;
            
            // If not found, try deep nested path
            if (!userId) {
              userId = activity.assignment?.selection?.marketplaceMember?.application?.candidate?.userId;
            }
            
            if (userId && userId !== 0) {
              userIds.add(userId);
            }
          });
          centralPSUserIds = Array.from(userIds);
          teamDataForEnrichment = teamData;
          
          console.log('📊 [WORKSMART-AI] Extracted My Team user IDs:', {
            count: centralPSUserIds.length,
            userIds: centralPSUserIds,
            sampleActivities: teamData.activities.slice(0, 3).map((a: any) => ({
              shallowUserId: a.assignment?.candidate?.userId,
              deepUserId: a.assignment?.selection?.marketplaceMember?.application?.candidate?.userId,
              candidateId: a.assignment?.candidate?.id,
              email: a.assignment?.candidate?.email,
              name: a.assignment?.candidate?.printableName
            }))
          });
        }
      }
      
      // Fetch Worksmart AI data from Lambda
      // For Central PS mode, don't pass managerId so backend uses 'central-ps' cache key
      // For My Team mode, pass managerId so backend uses team-specific cache key
      const worksmartResponse = await fetchWorksmartAIData(
        weekDate,
        centralPSUserIds,
        authState.token || undefined,
        dataMode === 'my-team' ? authState.managerId || undefined : undefined
      );
      
      console.log('📊 [WORKSMART-AI] Worksmart API response:', {
        dataCount: worksmartResponse.data.length,
        totalUsers: worksmartResponse.summary.totalUsers,
        cached: worksmartResponse.summary.cached,
        sampleUsers: worksmartResponse.data.slice(0, 3).map(u => ({ name: u.name, xoUserId: u.xoUserId }))
      });
      
      // Enrich with team context using the freshly fetched team data
      let enrichedData = worksmartResponse.data;
      
      if (teamDataForEnrichment && teamDataForEnrichment.activities) {
        console.log('📊 [WORKSMART-AI] Enriching Worksmart data with team context:', {
          dataMode,
          teamMembersCount: teamDataForEnrichment.activities.length
        });
        
        enrichedData = enrichWorksmartDataWithTeamContext(
          worksmartResponse.data,
          teamDataForEnrichment.activities
        );
      }
      
      // Sort the data
      const sortedData = sortWorksmartData(enrichedData, worksmartAISortBy);
      setWorksmartAIData(sortedData);
      
      // Calculate and set team statistics
      const stats = calculateWorksmartTeamStats(sortedData);
      setWorksmartAIStats(stats);
      
      console.log('✅ [WORKSMART-AI] Worksmart AI data loaded successfully:', {
        totalUsers: stats.totalUsers,
        averageAIRatio: stats.averageAIRatioPercent.toFixed(2) + '%',
        cached: worksmartResponse.summary.cached
      });
      
    } catch (error) {
      console.error('🚨 [WORKSMART-AI] Error loading Worksmart AI data:', error);
      console.error('🚨 [WORKSMART-AI] Error details:', {
        errorType: error instanceof Error ? error.constructor.name : typeof error,
        errorMessage: error instanceof Error ? error.message : String(error),
        errorStack: error instanceof Error ? error.stack : undefined
      });
      
      let errorMessage = 'Failed to load Worksmart AI data';
      if (error instanceof Error) {
        // Provide more specific error messages
        if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
          errorMessage = 'Network error: Unable to connect to Worksmart AI service. Please check your connection and try again.';
        } else if (error.message.includes('401')) {
          errorMessage = 'Authentication error: Please login again.';
        } else if (error.message.includes('403')) {
          errorMessage = 'Access denied: You do not have permission to access Worksmart AI data.';
        } else if (error.message.includes('404')) {
          errorMessage = 'Endpoint not found: Worksmart AI service may not be deployed.';
        } else if (error.message.includes('503') || error.message.includes('unavailable')) {
          errorMessage = 'Service unavailable: Worksmart AI database is not accessible.';
        } else {
          errorMessage = `Error: ${error.message}`;
        }
      }
      
      setWorksmartAIError(errorMessage);
    } finally {
      setWorksmartAILoading(false);
    }
  };

  /**
   * Handle Worksmart AI sorting
   */
  const handleWorksmartAISortChange = (field: keyof WorksmartAIMemberData) => {
    setWorksmartAISortBy(field);
    
    // Sort the Worksmart AI data
    const sortedData = sortWorksmartData(worksmartAIData, field);
    setWorksmartAIData(sortedData);
    
    console.log('📊 [WORKSMART-AI] Sorted by:', field);
  };

  /**
   * Main data loading effect
   */
  useEffect(() => {
    const loadData = async () => {
      if (!selectedWeek) return;
      
      // Require authentication for ALL modes
      if (!authState.isAuthenticated) {
        setError('Please login to access the leaderboard');
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        if (dataMode === 'basic') {
          await loadBasicData(selectedWeek);
        } else if (dataMode === 'central-ps') {
          await loadCentralPSData(selectedWeek);
        } else if (dataMode === 'my-team') {
          await loadMyTeamData(selectedWeek);
        }
      } catch (err) {
        console.error('Error loading data:', err);
          const errorMessage = err instanceof Error ? err.message : `Failed to load ${dataMode} data for the selected week`;
          setError(errorMessage);
          
          // If Central PS access was denied, switch back to basic mode
          if (dataMode === 'central-ps' && errorMessage.includes('Access denied')) {
            setDataMode('basic');
          }
      } finally {
        setLoading(false);
        
        // Log frontend cache statistics after data loading
        const frontendCache = getFrontendCache();
        frontendCache.logStats();
      }
    };

    loadData();
  }, [selectedWeek, dataMode, authState.isAuthenticated, reportingPeriod]);

  /**
   * Load Non-AI data when viewMode is non-ai-apps and we have the necessary data
   */
  useEffect(() => {
    if (viewMode === 'non-ai-apps' && selectedWeek && authState.isAuthenticated) {
      loadNonAIData(selectedWeek);
    }
  }, [viewMode, selectedWeek, dataMode, reportingPeriod, authState.isAuthenticated, authState.token, authState.managerId]);

  /**
   * Load Team AI data when team-ai view is selected
   * Team AI uses current date for rolling 4-week period (no week selector)
   */
  useEffect(() => {
    if (viewMode === 'team-ai' && authState.isAuthenticated) {
      // Use current date for rolling 4-week period
      const currentDate = new Date();
      const currentMonday = new Date(currentDate);
      currentMonday.setDate(currentDate.getDate() - currentDate.getDay() + 1);
      const currentWeekDate = currentMonday.toISOString().split('T')[0];
      
      loadTeamAIData(currentWeekDate);
    }
  }, [viewMode, dataMode, authState.isAuthenticated, authState.token, authState.managerId, teamAIPeriodMode]);

  /**
   * Load Worksmart AI data when worksmart-ai view is selected
   */
  useEffect(() => {
    if (viewMode === 'worksmart-ai' && selectedWeek && authState.isAuthenticated) {
      loadWorksmartAIData(selectedWeek);
    }
  }, [viewMode, selectedWeek, dataMode, authState.isAuthenticated, authState.token, authState.managerId]);

  /**
   * Re-filter data when data mode changes (only for Central PS mode using comprehensiveData)
   */
  useEffect(() => {
    if (dataMode !== 'central-ps' || !comprehensiveData || !authState.teamMembership?.hasCentralPSAccess) {
      return;
    }
    let cpsFilterManagerId: number;
    try {
      cpsFilterManagerId = getCentralPSFilterManagerId(authState);
    } catch {
      return;
    }

    console.log('🔄 [DATA MODE] Re-filtering Central PS data for mode:', dataMode);
    
    const filteredData = getFilteredLeaderboardData(
      comprehensiveData,
      users,
      cpsFilterManagerId,
      'central-ps',
      'global'
    );
    
    const finalData = filteredData.filter(user => user.cursorTimeMinutes > 0);
    
    setPersonalizedUsers(finalData);
    
    const filteredActivitiesForAI = filterActivitiesForCentralPS(
      comprehensiveData.activities,
      cpsFilterManagerId,
      comprehensiveData.teams,
      'global'
    );

    const processedAIData = processComprehensiveAIData(filteredActivitiesForAI);
    setAIData(processedAIData);
    
    console.log(`✅ [DATA MODE] Updated Central PS mode:`, {
      totalFilteredUsers: filteredData.length,
      finalUsers: finalData.length,
      cursorOnlyUsersRemoved: filteredData.length - finalData.length,
      aiUsersProcessed: filteredActivitiesForAI.length
    });
  }, [dataMode, comprehensiveData, authState.managerId, authState.teamMembership]);

  /**
   * Update sorting when metric changes OR when data is loaded OR when switching modes
   */
  useEffect(() => {
    if (dataMode !== 'basic' && personalizedUsers.length > 0) {
      const sortedData = sortPersonalizedLeaderboard(personalizedUsers, selectedMetric as keyof PersonalizedLeaderboardEntry);
      setPersonalizedUsers(sortedData);
      console.log('🔄 [SORT] Applied sorting to enhanced mode data:', selectedMetric);
    } else if (dataMode === 'basic' && users.length > 0) {
      const sortedData = sortUsersByMetric(users, selectedMetric as keyof Omit<LeaderboardEntry, 'userId' | 'email'>);
      setUsers(sortedData);
      console.log('🔄 [SORT] Applied sorting to basic mode data:', selectedMetric);
    }
  }, [selectedMetric, personalizedUsers.length, users.length, dataMode, reportingPeriod]);

  /**
   * Handle metric change
   */
  const handleMetricChange = (metric: string) => {
    setSelectedMetric(metric);
  };



  /**
   * Handle data mode changes
   */
  const handleDataModeChange = async (newMode: DataMode) => {
    // Check if the new mode is available for current view
    let availableModes: DataMode[];
    if (viewMode === 'cursor') {
      availableModes = getAvailableDataModes();
    } else if (viewMode === 'ai-overview') {
      availableModes = getAvailableAIDataModes();
    } else if (viewMode === 'non-ai-apps') {
      availableModes = getAvailableNonAIDataModes();
    } else if (viewMode === 'team-ai') {
      availableModes = getAvailableTeamAIDataModes();
    } else if (viewMode === 'worksmart-ai') {
      availableModes = getAvailableWorksmartAIDataModes();
    } else {
      availableModes = [];
    }
    
    if (!availableModes.includes(newMode)) {
      console.warn('Data mode', newMode, 'not available for current view', viewMode);
      return;
    }

    // If not authenticated, the login page will be shown automatically
    if (!authState.isAuthenticated) {
      setDataMode(newMode);
      console.log('Mode change to', newMode, 'will show login page');
      return;
    }

    setDataMode(newMode);
    console.log('Switched to data mode:', newMode);
  };

  /**
   * Handle view mode changes
   */
  const handleViewModeChange = (newViewMode: ViewMode) => {
    // Set appropriate default data mode for the new view
    const defaultDataMode = getDefaultDataMode(newViewMode);
    
    // Clear nonAI data when switching away from non-ai-apps
    if (viewMode === 'non-ai-apps' && newViewMode !== 'non-ai-apps') {
      console.log('🔄 [VIEW-SWITCH] Clearing non-AI data when switching away from non-ai-apps');
      setNonAIData({ teamMemberApplications: [], topApplications: [] });
      setNonAIError(null);
      setNonAILoading(false);
    }
    
    // Clear Team AI data when switching away from team-ai
    if (viewMode === 'team-ai' && newViewMode !== 'team-ai') {
      console.log('🔄 [VIEW-SWITCH] Clearing Team AI data when switching away from team-ai');
      setTeamAIData([]);
      setTeamAIError(null);
      setTeamAILoading(false);
      setTeamAIPeriodInfo(null);
      setTeamAIPeriodMode('current-board'); // Reset to default mode
    }
    
    // Clear Worksmart AI data when switching away from worksmart-ai
    if (viewMode === 'worksmart-ai' && newViewMode !== 'worksmart-ai') {
      console.log('🔄 [VIEW-SWITCH] Clearing Worksmart AI data when switching away from worksmart-ai');
      setWorksmartAIData([]);
      setWorksmartAIError(null);
      setWorksmartAILoading(false);
      setWorksmartAIStats({
        totalUsers: 0,
        averageAIRatio: 0,
        averageAIRatioPercent: 0,
        highestAIUser: null,
        lowestAIUser: null
      });
    }
    
    setViewMode(newViewMode);
    setDataMode(defaultDataMode);
    
    console.log('🔄 [VIEW-SWITCH] Switched to view mode:', newViewMode, 'with data mode:', defaultDataMode);
  };

  /**
   * Create export options for Cursor Leaderboard
   */
  const getLeaderboardExportOptions = (): LeaderboardExportOptions => ({
    dataMode,
    reportingPeriod,
    selectedWeek,
    selectedMetric,
    isPersonalized: dataMode !== 'basic'
  });

  /**
   * Create export options for AI Overview
   */
  const getAIOverviewExportOptions = (): AIOverviewExportOptions => ({
    dataMode,
    reportingPeriod,
    selectedWeek
  });

  /**
   * Create export options for Non-AI Apps
   */
  const getNonAIAppsExportOptions = (): NonAIAppsExportOptions => ({
    dataMode,
    reportingPeriod,
    selectedWeek
  });

  /**
   * Create export options for Team AI
   */
  const getTeamAIExportOptions = (): TeamAIExportOptions => ({
    dataMode,
    periodInfo: teamAIPeriodInfo || {
      startDate: '',
      endDate: '',
      weekDates: [],
      currentWeekIncluded: false,
      mode: 'current-board'
    },
    sortBy: teamAISortBy
  });

  /**
   * Create export options for Worksmart AI
   */
  const getWorksmartAIExportOptions = (): WorksmartAIExportOptions => ({
    dataMode,
    weekDate: selectedWeek,
    sortBy: worksmartAISortBy
  });

  /**
   * Handle period change with automatic date adjustment
   */
  const handlePeriodChange = (newPeriod: ReportingPeriod) => {
    console.log('📊 [PERIOD-CHANGE] Changing period from', reportingPeriod, 'to', newPeriod);
    
    setReportingPeriod(newPeriod);
    
    // If switching to 4-weekly mode, check if current date needs adjustment
    if (newPeriod === '4weekly' && selectedWeek) {
      const needsAdjustment = !isAtLeastFourWeeksBack(selectedWeek);
      
      if (needsAdjustment) {
        const fourWeeklyStartDate = getFourWeeklyStartMonday();
        console.log('📅 [PERIOD-CHANGE] Adjusting date for 4-weekly mode from', selectedWeek, 'to', fourWeeklyStartDate);
        setSelectedWeek(fourWeeklyStartDate);
      } else {
        console.log('📅 [PERIOD-CHANGE] Current date', selectedWeek, 'is already suitable for 4-weekly mode');
      }
    }
  };

  // Show login page if not authenticated (required for all modes)
  if (!authState.isAuthenticated) {
    return (
      <div className="app">
        <header>
          <h1>AI Leaderboard</h1>
        </header>
        
        <main>
          <LoginPage onAuthSuccess={handleAuthSuccess} />
        </main>
        
        <footer>
          <p>AI Leaderboard - Analytics Dashboard</p>
        </footer>
      </div>
    );
  }

  // Determine current data and mode info
  const isEnhancedMode = dataMode !== 'basic';
  const currentUsers = isEnhancedMode ? personalizedUsers : basicUsers;
  const hasData = currentUsers.length > 0;
  


  return (
    <div className="app fullscreen">
      <header>
        <h1>AI Leaderboard</h1>
        {authState.isAuthenticated && (
          <UserInfoBar 
            authState={authState} 
            onLogout={handleLogout}
          />
        )}
      </header>
      
      <div className="app-layout">
        <div className="vertical-menu">
          {/* Cursor Tab - Always visible when authenticated */}
          <button
            onClick={() => handleViewModeChange('cursor')}
            className={`menu-tab ${viewMode === 'cursor' ? 'active' : ''}`}
            title="AI tool usage and cursor statistics"
          >
            <span className="tab-icon">🖱️</span>
            <span className="tab-label">Cursor</span>
          </button>

          {/* AI Overview Tab - Only for managers */}
          {isAIOverviewAvailable() && (
            <button
              onClick={() => handleViewModeChange('ai-overview')}
              className={`menu-tab ${viewMode === 'ai-overview' ? 'active' : ''}`}
              title="AI tool usage overview from team activities"
            >
              <span className="tab-icon">🤖</span>
              <span className="tab-label">AI Overview</span>
            </button>
          )}

          {/* Non-AI Apps Tab - Only for managers */}
          {isNonAIAppsAvailable() && (
            <button
              onClick={() => handleViewModeChange('non-ai-apps')}
              className={`menu-tab ${viewMode === 'non-ai-apps' ? 'active' : ''}`}
              title="Non-AI applications used by team members"
            >
              <span className="tab-icon">📱</span>
              <span className="tab-label">Non-AI Apps</span>
            </button>
          )}

          {/* Team AI Leaderboard Tab - Only for managers */}
          {isTeamAIAvailable() && (
            <button
              onClick={() => handleViewModeChange('team-ai')}
              className={`menu-tab ${viewMode === 'team-ai' ? 'active' : ''}`}
              title="Team AI effectiveness leaderboard - rolling 4-week analysis"
            >
              <span className="tab-icon">🏆</span>
              <span className="tab-label">Team AI Leaderboard</span>
            </button>
          )}

          {/* Worksmart AI Tab - Only for managers */}
          {isWorksmartAIAvailable() && (
            <button
              onClick={() => handleViewModeChange('worksmart-ai')}
              className={`menu-tab ${viewMode === 'worksmart-ai' ? 'active' : ''}`}
              title="Worksmart AI usage report - weekly AI tool usage from timecard tags"
            >
              <span className="tab-icon">📊</span>
              <span className="tab-label">Worksmart AI</span>
            </button>
          )}
        </div>

        <div className="main-content">
          <div className="content-header">
            {/* Hide week selector and period toggle for Team AI - it has a fixed rolling 4-week period */}
            {viewMode !== 'team-ai' && (
              <div className="selector-row">
                <WeekSelector 
                  onWeekSelect={setSelectedWeek} 
                  selectedWeek={selectedWeek} 
                  reportingPeriod={reportingPeriod}
                />
                
                {/* Period Toggle for Weekly/4-Weekly - hide for Worksmart AI (weekly only) */}
                {viewMode !== 'worksmart-ai' && (
                  <PeriodToggle 
                    currentPeriod={reportingPeriod}
                    onPeriodChange={handlePeriodChange}
                    disabled={loading}
                  />
                )}
              </div>
            )}
            
            {/* Show horizontal tabs for data modes based on current view */}
            <HorizontalTabs
              activeTab={dataMode}
              onTabChange={handleDataModeChange}
              availableModes={
                viewMode === 'cursor' 
                  ? getAvailableDataModes() 
                  : viewMode === 'ai-overview'
                  ? getAvailableAIDataModes()
                  : viewMode === 'non-ai-apps'
                  ? getAvailableNonAIDataModes()
                  : viewMode === 'team-ai'
                  ? getAvailableTeamAIDataModes()
                  : viewMode === 'worksmart-ai'
                  ? getAvailableWorksmartAIDataModes()
                  : []
              }
              authState={authState}
              viewMode={viewMode}
            />
          </div>

          <div className="content-body">
            {loading && <div className="status-message loading">Loading data...</div>}
            {error && <div className="status-message error">{error}</div>}
            
            {!loading && !error && hasData && viewMode === 'cursor' && (
              <>
                <div className="metric-controls">
                  <MetricSelector
                    currentMetric={selectedMetric}
                    onMetricChange={handleMetricChange}
                  />
                  

                </div>
                
                <div className="leaderboard-content">
                  <div className="chart-container">
                    <LeaderboardChart
                      users={currentUsers as LeaderboardEntry[]}
                      metric={selectedMetric as keyof Omit<LeaderboardEntry, 'userId' | 'email'>}
                      metricName={selectedMetric}
                    />
                  </div>
                  
                  <div className="table-container">
                    <LeaderboardTable
                      users={currentUsers}
                      sortBy={selectedMetric}
                      onSortChange={handleMetricChange}
                      isPersonalized={isEnhancedMode}
                      exportOptions={getLeaderboardExportOptions()}
                      cycleDateRange={spendCycleDateRange}
                    />
                  </div>
                </div>
              </>
            )}
            
            {!loading && !error && hasData && viewMode === 'ai-overview' && aiData && (
              <AIOverviewTable 
                aiData={aiData} 
                exportOptions={getAIOverviewExportOptions()}
              />
            )}
            
            {!loading && !error && viewMode === 'ai-overview' && !aiData && (
              <div className="status-message no-data">
                AI Overview data is not available for the selected period.
              </div>
            )}

            {/* Non-AI Apps Section */}
            {viewMode === 'non-ai-apps' && (
              <NonAIAppsSection 
                nonAIData={nonAIData}
                loading={nonAILoading}
                error={nonAIError}
                viewMode={dataMode}
                exportOptions={getNonAIAppsExportOptions()}
              />
            )}

            {/* Team AI Leaderboard Section */}
            {viewMode === 'team-ai' && (
              <TeamAISection 
                teamAIData={teamAIData}
                loading={teamAILoading}
                error={teamAIError}
                periodInfo={teamAIPeriodInfo || {
                  startDate: '',
                  endDate: '',
                  weekDates: [],
                  currentWeekIncluded: false,
                  mode: 'current-board'
                }}
                onSortChange={handleTeamAISortChange}
                sortBy={teamAISortBy}
                exportOptions={getTeamAIExportOptions()}
                periodMode={teamAIPeriodMode}
                onPeriodModeChange={handleTeamAIPeriodModeChange}
              />
            )}

            {/* Worksmart AI Section */}
            {viewMode === 'worksmart-ai' && (
              <WorksmartAISection 
                worksmartData={worksmartAIData}
                teamStats={worksmartAIStats}
                loading={worksmartAILoading}
                error={worksmartAIError}
                weekDate={selectedWeek}
                onSortChange={handleWorksmartAISortChange}
                sortBy={worksmartAISortBy}
                exportOptions={getWorksmartAIExportOptions()}
              />
            )}
            
            {!loading && !error && !hasData && viewMode !== 'worksmart-ai' && (
              <div className="status-message no-data">
                No data available for the selected week
                {isEnhancedMode && ' (or no team members matched)'}
              </div>
            )}
          </div>
        </div>
      </div>
      

    </div>
  );
}

export default App; 